import React from 'react';
import { Heart, Users, BookOpen, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { courses } from '../mock/data';

const iconMap = {
  Heart: Heart,
  Users: Users,
  BookOpen: BookOpen
};

const Courses = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="courses" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
            Subject Areas
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Specialist Tutoring in Three Key Areas
          </h2>
          <p className="text-lg text-gray-600">
            Comprehensive support across all major topics within Health & Social Care, 
            Counselling, and Social Sciences
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {courses.map((course) => {
            const IconComponent = iconMap[course.icon];
            return (
              <Card 
                key={course.id} 
                className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-teal-200 cursor-pointer"
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors duration-300">
                    <IconComponent className="h-7 w-7 text-teal-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-2xl mb-2">{course.title}</CardTitle>
                  <CardDescription className="text-base">
                    {course.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Key Modules */}
                    <div>
                      <h4 className="font-semibold text-sm text-gray-700 mb-3">Key Topics Covered:</h4>
                      <ul className="space-y-2">
                        {course.modules.map((module, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                            <span className="text-teal-600 mt-1">•</span>
                            <span>{module}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Course Info */}
                    <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                      <div>
                        <p className="text-xs text-gray-500">Level</p>
                        <p className="text-sm font-semibold text-gray-900">{course.level}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Schedule</p>
                        <p className="text-sm font-semibold text-gray-900">{course.duration}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Not Sure Which Course You Need?
          </h3>
          <p className="text-teal-100 mb-6 max-w-2xl mx-auto">
            Book a free 15-minute consultation to discuss your specific needs and how I can help you succeed
          </p>
          <Button 
            size="lg"
            onClick={scrollToContact}
            className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-6 text-lg group"
          >
            Schedule Free Consultation
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Courses;
